import type { PersonConfig, PersonField as PersonConfigField } from '@lighthouse/core'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAsyncRetry } from 'react-use'

import { useApplicationContext } from '../../../../contexts'
import { useAppThemeContext } from '../../../ApplicationContainer'
import { applyFillPickerValue4CssColorValue } from '../../../FillPicker'
import { PersonSelect } from '../../../PersonSelect'
import { minPopoverWidth } from '../../constant'
import type { FieldBaseProps } from '../../types'
import * as SC from './styles'

interface PersonFieldProps extends FieldBaseProps {
    value: string[]
    personConfig?: PersonConfig
    personField?: PersonConfigField
}

const PersonField: React.FunctionComponent<PersonFieldProps> = ({
    value: data,
    personConfig,
    isControlled,
    readOnly,
    config,
    personField,
    onFetchPersonOptions,
    onChange
}) => {
    const { theme } = useAppThemeContext()
    const { placeholder, person: personInputConfig, breakPoint } = personConfig ?? {}
    const { person } = personField ?? {}
    const { multiple } = person ?? {}
    const { filter } = personInputConfig ?? {}
    const { personOptions, pageTarget } = useApplicationContext()
    const placeholderColor = applyFillPickerValue4CssColorValue(breakPoint?.design?.placeholderColor?.color, theme.palettes)
    const { value: filterPersonOptions } = useAsyncRetry(() => {
        if (!filter || (filter.expression?.conditions ?? []).length === 0 || !onFetchPersonOptions) {
            return Promise.resolve(undefined)
        }
        return onFetchPersonOptions?.(filter)
    }, [filter, onFetchPersonOptions])

    const currentPersonOptions = useMemo(() => filterPersonOptions || personOptions, [filterPersonOptions, personOptions])

    const { t } = useTranslation()

    const [value, setValue] = useState(data ?? [])

    const [opened, setOpened] = useState(false)

    const usedValue = useMemo(() => (isControlled ? data : value), [data, isControlled, value])
    const handleMultiChange = useCallback(
        (value: string[]) => {
            setValue(value)
            onChange?.({ type: 'person', value })
        },
        [onChange]
    )
    const containerRef = useRef<HTMLDivElement>(null)

    const dropDownWidth = containerRef?.current?.getBoundingClientRect()?.width ?? minPopoverWidth

    return (
        <SC.Container ref={containerRef} data-field-input data-field-active={opened} data-field-disabled={!!readOnly}>
            <PersonSelect
                pageTarget={pageTarget}
                options={currentPersonOptions}
                value={usedValue}
                tagClearable
                withinPortal
                readOnly={readOnly}
                disabled={readOnly}
                clearable={false}
                isHiddenUserId
                enableCheckBox={false}
                isMultiple={multiple}
                placeholder={placeholder}
                placeholderColor={placeholderColor}
                searchPlaceholder={t('personNameOrPersonId')}
                emptyDesc={t('noFindPerson')}
                width={`${dropDownWidth}px`}
                minWidth={380}
                isBorder={false}
                isWrap
                styles={{
                    value: {
                        alignItems: 'flex-start',
                        padding: '0 8px'
                    }
                }}
                onVisibleChange={setOpened}
                onChange={handleMultiChange}
            />
        </SC.Container>
    )
}

export default PersonField
